import { logger } from '@/lib';
import { IMAGE_CONFIG } from '@/lib';
import { 
  OptimizedVariantType, 
  getVariantConfig, 
  calculateCoverCrop,
  calculateCoverDimensions,
  calculateOptimalDimensions
} from './optimizedVariants';

// Create a reusable canvas element for better performance (lazy initialization)
let canvas: HTMLCanvasElement | null = null;
let ctx: CanvasRenderingContext2D | null = null;

const getCanvasContext = (): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } => {
  if (!canvas || !ctx) {
    canvas = document.createElement('canvas');
    ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }
  }
  return { canvas, ctx };
};

/**
 * Enhanced image resizing function with optimized variant support
 * @param base64Image - Base64 encoded image string
 * @param variant - Optimized variant type
 * @returns Promise<{ resizedImage: string; compressionInfo: CompressionInfo }> - Resized image and compression stats
 */
export const resizeImageOptimized = async (
  base64Image: string, 
  variant: OptimizedVariantType
): Promise<{ 
  resizedImage: string; 
  compressionInfo: CompressionInfo;
  originalDimensions: { width: number; height: number };
  finalDimensions: { width: number; height: number };
}> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const config = getVariantConfig(variant);
    
    img.onerror = () => reject(new Error('Failed to load image'));
    
    img.onload = () => {
      try {
        const originalDimensions = { width: img.width, height: img.height };

        // `cover` variants (the thumbnail) centre-crop to the target aspect
        // ratio; everything else fits inside the box as before.
        const isCover = config.fit === 'cover';

        const finalDimensions = isCover
          ? calculateCoverDimensions(img.width, img.height, config.width, config.height)
          : calculateOptimalDimensions(img.width, img.height, config.width, config.height);

        const crop = isCover
          ? calculateCoverCrop(img.width, img.height, config.width, config.height)
          : { sx: 0, sy: 0, sWidth: img.width, sHeight: img.height };

        // Get canvas context (lazy initialization)
        const { canvas: canvasElement, ctx: canvasCtx } = getCanvasContext();
        
        // Set canvas dimensions
        canvasElement.width = finalDimensions.width;
        canvasElement.height = finalDimensions.height;

        // Clear canvas before drawing
        canvasCtx.clearRect(0, 0, finalDimensions.width, finalDimensions.height);
        
        // Use better image rendering
        canvasCtx.imageSmoothingEnabled = true;
        canvasCtx.imageSmoothingQuality = 'high';
        
        // Draw image with proper scaling, taking only the crop rectangle. For
        // `contain` that rectangle is the whole image, so this is equivalent to
        // the previous 5-argument call.
        canvasCtx.drawImage(
          img,
          crop.sx,
          crop.sy,
          crop.sWidth,
          crop.sHeight,
          0,
          0,
          finalDimensions.width,
          finalDimensions.height
        );
        
        // Convert to base64 with quality setting
        const resizedImage = canvasElement.toDataURL(`image/${config.format}`, config.quality);
        
        // Calculate compression info
        const originalSize = base64Image.length;
        const compressedSize = resizedImage.length;
        const compressionRatio = Math.round(((originalSize - compressedSize) / originalSize) * 100);
        
        const compressionInfo: CompressionInfo = {
          originalSize,
          compressedSize,
          compressionRatio,
          variant,
          quality: config.quality,
          format: config.format
        };

        resolve({ 
          resizedImage, 
          compressionInfo,
          originalDimensions,
          finalDimensions
        });
      } catch (error) {
        reject(error);
      }
    };

    img.src = base64Image;
  });
};



/**
 * Process multiple image variants efficiently
 * @param base64Image - Base64 encoded image string
 * @param variants - Array of variant types to generate
 * @returns Promise<ProcessedVariants> - Object with all processed variants
 */
export const processImageVariants = async (
  base64Image: string,
  variants: OptimizedVariantType[]
): Promise<Partial<ProcessedVariants>> => {
  const results: Partial<ProcessedVariants> = {};
  
  // Process variants sequentially to avoid overwhelming the browser
  for (const variant of variants) {
    try {
      const result = await resizeImageOptimized(base64Image, variant);
      results[variant] = result;
    } catch (error) {
      logger.error(`Failed to process variant ${variant}:`, error);
      throw new Error(`Failed to process ${variant} variant: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  
  return results;
};

/**
 * Convert base64 string to Blob object with optimized MIME type detection
 * @param base64Image - Base64 encoded image string
 * @returns Blob object
 */
export const base64ToBlob = (base64Image: string): Blob => {
  const arr = base64Image.split(',');
  const mimeMatch = arr[0].match(/:(.*?);/);
  
  // Default to JPEG if MIME type detection fails
  const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
  
  const bstr = atob(arr[1]);
  const u8arr = new Uint8Array(bstr.length);
  
  for (let i = 0; i < bstr.length; i++) {
    u8arr[i] = bstr.charCodeAt(i);
  }
  
  return new Blob([u8arr], { type: mime });
};

/**
 * Get image dimensions from base64 string
 * @param base64Image - Base64 encoded image string
 * @returns Promise<{ width: number; height: number }> - Image dimensions
 */
export const getImageDimensions = (base64Image: string): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image for dimension calculation'));
    };
    
    img.src = base64Image;
  });
};

/**
 * Validate image before processing
 * @param base64Image - Base64 encoded image string
 * @returns Promise<ImageValidationResult> - Validation result
 */
export const validateImage = async (base64Image: string): Promise<ImageValidationResult> => {
  try {
    // Check if it's a valid base64 image
    if (!base64Image.startsWith('data:image/')) {
      return { isValid: false, error: 'Invalid image format' };
    }
    
    // Get image dimensions
    const dimensions = await getImageDimensions(base64Image);
    
    // Check minimum dimensions
    if (dimensions.width < IMAGE_CONFIG.MIN_DIMENSION || dimensions.height < IMAGE_CONFIG.MIN_DIMENSION) {
      return { isValid: false, error: `Image too small (minimum ${IMAGE_CONFIG.MIN_DIMENSION}x${IMAGE_CONFIG.MIN_DIMENSION} pixels)` };
    }
    
    // Check maximum dimensions
    if (dimensions.width > IMAGE_CONFIG.MAX_DIMENSION || dimensions.height > IMAGE_CONFIG.MAX_DIMENSION) {
      return { isValid: false, error: `Image too large (maximum ${IMAGE_CONFIG.MAX_DIMENSION}x${IMAGE_CONFIG.MAX_DIMENSION} pixels)` };
    }
    
    // Check file size (rough estimate from base64 length)
    const estimatedSize = base64Image.length * IMAGE_CONFIG.BASE64_OVERHEAD_FACTOR; // Base64 is ~33% larger than binary
    if (estimatedSize > IMAGE_CONFIG.MAX_FILE_SIZE_MB * 1024 * 1024) {
      return { isValid: false, error: `Image file too large (maximum ${IMAGE_CONFIG.MAX_FILE_SIZE_MB}MB)` };
    }
    
    return { 
      isValid: true, 
      dimensions,
      estimatedSize: Math.round(estimatedSize)
    };
  } catch (error) {
    return { 
      isValid: false, 
      error: error instanceof Error ? error.message : 'Unknown validation error' 
    };
  }
};

// Type definitions for enhanced functionality
export interface CompressionInfo {
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  variant: OptimizedVariantType;
  quality: number;
  format: 'jpeg' | 'webp';
}

export interface ProcessedVariant {
  resizedImage: string;
  compressionInfo: CompressionInfo;
  originalDimensions: { width: number; height: number };
  finalDimensions: { width: number; height: number };
}

export type ProcessedVariants = Record<OptimizedVariantType, ProcessedVariant>;

export interface ImageValidationResult {
  isValid: boolean;
  error?: string;
  dimensions?: { width: number; height: number };
  estimatedSize?: number;
} 